import React, { useState, useEffect } from 'react';
import { useClient } from '../../context/ClientContext';

const MATCH_TYPE_BADGE = {
  EXACT: { label: 'exact', bg: 'bg-blue-100', text: 'text-blue-700' },
  PHRASE: { label: 'phrase', bg: 'bg-purple-100', text: 'text-purple-700' },
  BROAD: { label: 'broad', bg: 'bg-gray-100', text: 'text-gray-600' }
};

export default function TopKeywords({ showWasted = false, dateRange }) {
  const { brand, dataSources, fetcher } = useClient();
  const currencySymbol = dataSources?.googleAds?.currencySymbol || brand?.currencySymbol || '$';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('top');

  useEffect(() => {
    setLoading(true);
    fetcher.fetchTopKeywords(dateRange)
      .then(setData)
      .finally(() => setLoading(false));
  }, [dateRange]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-8 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  const hasTopKeywords = data?.keywords?.length > 0;
  const hasWastedKeywords = data?.wastedKeywords?.length > 0;

  if (!hasTopKeywords && !hasWastedKeywords) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide"
            style={{ fontFamily: brand.typography?.headerFont }}>
          Search Terms
        </h3>
        <p className="text-gray-500 mt-2">No keyword data available yet.</p>
      </div>
    );
  }

  const showTabs = showWasted && hasWastedKeywords;

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide"
              style={{ fontFamily: brand.typography?.headerFont }}>
            {showTabs ? 'Search Term Analysis' : 'Top Converting Keywords'}
          </h3>
          <p className="text-xs text-gray-400 mt-1">Non-brand search terms</p>
        </div>
      </div>

      {showTabs && (
        <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5 mb-4 w-fit">
          <button
            onClick={() => setActiveTab('top')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              activeTab === 'top' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Top Converting
          </button>
          <button
            onClick={() => setActiveTab('wasted')}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              activeTab === 'wasted' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Wasted Spend
          </button>
        </div>
      )}

      {activeTab === 'top' && (
        <div className="space-y-2">
          {data.keywords.map((kw, idx) => {
            const badge = kw.matchType ? MATCH_TYPE_BADGE[kw.matchType] : null;
            return (
              <div key={idx} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                <span className="text-xs font-bold text-gray-400 w-5 text-center">{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">{kw.keyword}</p>
                  {kw.matchedKeyword && kw.matchedKeyword !== kw.keyword && (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-xs text-gray-400">matched:</span>
                      <span className="text-xs text-gray-500 truncate">{kw.matchedKeyword}</span>
                    </div>
                  )}
                </div>
                {badge && (
                  <span className={`${badge.bg} ${badge.text} text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0`}>
                    {badge.label}
                  </span>
                )}
                <div className="flex items-center gap-4 flex-shrink-0 text-right">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{kw.enquiries}</p>
                    <p className="text-xs text-gray-400">enquiries</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">{kw.clicks}</p>
                    <p className="text-xs text-gray-400">clicks</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">{currencySymbol}{kw.costPerEnquiry.toFixed(0)}</p>
                    <p className="text-xs text-gray-400">per enq.</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">
                      {kw.clicks > 0 ? `${((kw.enquiries / kw.clicks) * 100).toFixed(1)}%` : '\u2014'}
                    </p>
                    <p className="text-xs text-gray-400">conv. rate</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'wasted' && hasWastedKeywords && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 mb-3">
            Search terms with clicks but zero conversions — potential negatives
          </p>
          {data.wastedKeywords.map((kw, idx) => {
            const badge = kw.matchType ? MATCH_TYPE_BADGE[kw.matchType] : null;
            return (
              <div key={idx} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                <span className="text-xs font-bold text-red-400 w-5 text-center">{idx + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 truncate">{kw.keyword}</p>
                  {kw.matchedKeyword && kw.matchedKeyword !== kw.keyword && (
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-xs text-gray-400">matched:</span>
                      <span className="text-xs text-gray-500 truncate">{kw.matchedKeyword}</span>
                    </div>
                  )}
                </div>
                {badge && (
                  <span className={`${badge.bg} ${badge.text} text-[10px] font-medium px-1.5 py-0.5 rounded flex-shrink-0`}>
                    {badge.label}
                  </span>
                )}
                <div className="flex items-center gap-4 flex-shrink-0 text-right">
                  <div>
                    <p className="text-sm font-semibold text-red-600">{currencySymbol}{kw.cost.toFixed(0)}</p>
                    <p className="text-xs text-gray-400">wasted</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-600">{kw.clicks}</p>
                    <p className="text-xs text-gray-400">clicks</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
